/**
 * Cinema Stage — Mux-first live video orchestrator.
 *
 * Replaces the legacy LiveKit/mesh <StreamVideo> render in the live show.
 * Behavior:
 *   - SELLER: when their stream is live, auto-publishes their phone camera
 *             to Mux at 4K via WHIP. Reverts to <StreamVideo> (LiveKit /
 *             mesh) only if Mux is unavailable or the WHIP publish fails.
 *   - VIEWER: polls the backend every 3s for an active Mux playback URL.
 *             When found, swaps in <CinemaViewer> for ~3-5s latency,
 *             buttery-smooth HLS — automatically.  No toggle. No manual
 *             intervention.  Falls back to the existing LiveKit / mesh
 *             video if Mux isn't ready yet (the first ~10s after the
 *             seller taps "Tap to go live").
 *
 * Cost guard-rail: backend env vars `MUX_MAX_RESOLUTION` and
 * `MUX_LATENCY_MODE` let you downgrade to 1080p / standard latency in
 * one redeploy if Cinema bills get out of hand.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import StreamVideo from "@/components/StreamVideo";
import { CinemaViewer, useCinemaSeller } from "@/components/CinemaMode";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 3000;
// Fast initial polls for the first ~3 seconds of viewer entry. Without
// these the Mux playback URL only appears on the regular 3-second
// cadence, leaving viewers staring at the LiveKit fallback longer
// than necessary when the seller is broadcasting via Cinema 4K.
const FAST_POLL_BACKOFF_MS = [400, 800, 1500];

export default function CinemaStage({
  stream,
  user,
  wsRef,
  myConnIdRef,
  setVideoBackend,
  setCameraOn,
}) {
  const streamId = stream.id;
  const isSeller = !!(
    user &&
    (user.user_id === stream.seller_id ||
      (stream.cohost_ids || []).includes(user.user_id))
  );
  const isLive = stream.status === "live";

  const cinema = useCinemaSeller(streamId);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [muxEnabled, setMuxEnabled] = useState(null); // null = unknown
  const [isPlus, setIsPlus] = useState(false);
  const [cinemaActive, setCinemaActive] = useState(false);
  const [cinemaError, setCinemaError] = useState(null);
  const startedRef = useRef(false);

  // Check if Mux is configured at all. Done once per session.
  useEffect(() => {
    let alive = true;
    api.get("/mux/config")
      .then(({ data }) => { if (alive) setMuxEnabled(!!data?.enabled); })
      .catch(() => { if (alive) setMuxEnabled(false); });
    return () => { alive = false; };
  }, []);

  // Sellers: check Cinema-mode eligibility (Plus subscription OR admin
  // grant). The backend exposes this as a single endpoint so we don't
  // have to combine two states client-side.
  useEffect(() => {
    if (!isSeller || !user) return;
    let alive = true;
    api.get("/me/cinema-status")
      .then(({ data }) => { if (alive) setIsPlus(!!data?.eligible); })
      .catch(() => { if (alive) setIsPlus(false); });
    return () => { alive = false; };
  }, [isSeller, user]);

  // Viewer: poll for an active Mux playback URL while the stream is live.
  // First match wins → swap in CinemaViewer. We keep polling at a slower
  // cadence afterwards in case the Mux stream restarts mid-show. The
  // first three polls run on a fast backoff (400/800/1500ms) so a
  // viewer who lands during the seller's cinema-warm-up sees the HLS
  // playlist within ~1.5s instead of waiting a full 3s tick.
  useEffect(() => {
    if (isSeller || !isLive || muxEnabled !== true) return undefined;
    let alive = true;
    let timer = null;
    let attempt = 0;

    const tick = async () => {
      try {
        const { data } = await api.get(`/mux/streams/${streamId}/playback`);
        if (!alive) return;
        if (data?.playback_url) {
          setPlaybackUrl(data.playback_url);
        } else {
          setPlaybackUrl(null);
        }
      } catch (_) { /* keep retrying */ }
      const delay =
        attempt < FAST_POLL_BACKOFF_MS.length
          ? FAST_POLL_BACKOFF_MS[attempt]
          : POLL_INTERVAL_MS;
      attempt += 1;
      timer = setTimeout(tick, delay);
    };
    tick();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [isSeller, isLive, muxEnabled, streamId]);

  // Seller: Cinema mode auto-starts when their camera goes live IF
  // they're Plus-eligible. The "Tap to go live" gesture inside
  // <StreamVideo> grants media permission; we hook the same user-
  // gesture moment to fire the Mux WHIP publish so there's no
  // separate toggle. Non-Plus sellers fall through to the standard
  // LiveKit/mesh path and see a clickable Cinema rail badge that
  // opens an in-stream Plus upgrade sheet.
  const [localCameraOn, setLocalCameraOn] = useState(false);
  const handleCameraOn = (on) => {
    setLocalCameraOn(!!on);
    setCameraOn?.(on);
  };

  // Cinema 4K bootstrap. LiveKit/mesh is already publishing in parallel,
  // so any Mux failure here is non-blocking for the actual broadcast.
  //
  // Resilience strategy (in order):
  //   1. First attempt fires the moment the seller's camera comes on.
  //   2. On a transient failure (502/503/504/network/timeout) we auto-retry
  //      twice with exponential backoff (3s → 8s). Most production Mux
  //      blips clear within 5 seconds.
  //   3. After 3 total attempts we surface a friendly fallback banner that
  //      includes a manual "Try again" affordance — so the seller is never
  //      stuck waiting on a page refresh.
  //   4. 402 (Plus required) short-circuits all of the above with the
  //      upgrade nudge from the backend. 403/404/5xx-with-business-detail
  //      also skip retries (those won't fix themselves).
  const attemptsRef = useRef(0);
  const retryTimerRef = useRef(null);

  const tryStartCinema = useCallback(async () => {
    try {
      await cinema.start();
      setCinemaActive(true);
      setCinemaError(null);
      attemptsRef.current = 0;
    } catch (e) {
      const status = e?.response?.status;
      const detailMsg = e?.response?.data?.detail?.message;
      const isTransient =
        !status ||                  // network/timeout — no response
        status === 502 ||
        status === 503 ||
        status === 504 ||
        status === 408 ||
        status === 429;
      attemptsRef.current += 1;
      // Hard fail — don't retry on auth/config issues.
      if (!isTransient || attemptsRef.current >= 3) {
        const msg =
          status === 402 && detailMsg
            ? detailMsg
            : status === 503
              ? "Cinema 4K isn't configured on this server. Streaming continues in standard mode."
              : "Cinema 4K is unavailable right now — tap to retry. Standard streaming is on.";
        setCinemaError(msg);
        return;
      }
      // Schedule the next attempt — 3s, then 8s. Squelch the banner
      // during retries so the seller doesn't see a flash of failure for
      // a glitch that auto-resolves.
      const backoff = attemptsRef.current === 1 ? 3000 : 8000;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        // The bootstrap effect re-runs whenever startedRef flips back to
        // false. We do that here so the same code path drives the retry.
        startedRef.current = false;
        // Force-bump a ref so React re-evaluates the effect's predicate.
        // (cinemaActive is still false, so the effect will fire again.)
        setCinemaError(null);
      }, backoff);
    }
  }, [cinema]);

  useEffect(() => {
    if (!isSeller || !isLive || !isPlus || muxEnabled !== true) return;
    if (!localCameraOn || cinemaActive || startedRef.current) return;
    startedRef.current = true;
    tryStartCinema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeller, isLive, isPlus, muxEnabled, localCameraOn, cinemaActive, cinemaError]);

  // Clean up any pending retry on unmount.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // Manual retry handler attached to the fallback banner.
  const onManualRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    attemptsRef.current = 0;
    startedRef.current = false;
    setCinemaError(null);
    // The effect predicate will re-fire because cinemaError changed.
  }, []);

  // Auto-dismiss the Cinema-error banner after 10 seconds so it never
  // sits on top of the header forever. The seller can read it, tap
  // "Try again" if they want, and then it gets out of the way.
  useEffect(() => {
    if (!cinemaError) return undefined;
    const t = setTimeout(() => setCinemaError(null), 10000);
    return () => clearTimeout(t);
  }, [cinemaError]);

  useEffect(() => {
    return () => {
      if (cinemaActive) {
        cinema.stop().catch(() => { /* ignore */ });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // VIEWER + Mux is producing playback → use the CinemaViewer.
  if (!isSeller && playbackUrl) {
    return <CinemaViewer playbackUrl={playbackUrl} posterUrl={stream.seller_picture} />;
  }

  // Everyone else: fall back to LiveKit / mesh stack. The seller still
  // renders this because the LocalVideoPreview gives them their own
  // camera feedback (Mux doesn't echo a self-preview back over HLS in
  // real time — 3-5s delay would be disorienting for the seller).
  return (
    <>
      <StreamVideo
        streamId={streamId}
        isSeller={isSeller}
        wsRef={wsRef}
        myConnIdRef={myConnIdRef}
        isLive={isLive}
        onBackendChosen={setVideoBackend}
        onCameraOn={handleCameraOn}
        sellerName={stream.seller_name}
        sellerPicture={stream.seller_picture}
        portrait
      />
      {/* Seller Cinema-mode toggle removed from the on-stream UI.
          A non-clickable amber "Cinema 4K" sparkle badge on the right
          rail now signals when Cinema is available for this seller;
          non-Plus sellers see a clickable rail badge instead that
          opens an in-stream PlusUpgradeSheet (no navigation away). */}
      {isSeller && cinemaError && (
        <button
          type="button"
          onClick={onManualRetry}
          className="absolute left-1/2 -translate-x-1/2 z-30 max-w-[80%] text-center px-3.5 py-1.5 rounded-full bg-zinc-900/90 backdrop-blur border border-amber-400/40 text-white text-[11px] font-semibold shadow-lg hover:bg-zinc-800/90 active:scale-95 transition flex items-center gap-2"
          style={{ top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 88px)" }}
          data-testid="cinema-error"
          aria-label="Cinema 4K unavailable — tap to retry"
        >
          <span>{cinemaError}</span>
          <span className="text-amber-300 font-black tracking-wider">↻</span>
        </button>
      )}
      {/* "Streaming in 4K Cinema mode" success pill — small, discreet, on
          the seller's own preview only. Confirms in real time that their
          premium pipeline is actually engaged so they don't have to wonder
          whether viewers are getting Cinema or fallback playback. Pulses
          gently to read as live, never blocks taps (pointer-events-none). */}
      {isSeller && cinemaActive && !cinemaError && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-amber-300/40 text-white text-[10px] font-bold tracking-wide shadow"
          style={{ top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 88px)" }}
          data-testid="cinema-active-pill"
          aria-label="Streaming in Cinema 4K mode"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
          </span>
          <span className="text-amber-200">4K Cinema</span>
          <span className="text-white/70">· live</span>
        </div>
      )}
    </>
  );
}
